import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  FileText,
  HelpCircle,
  PhoneCall,
  RotateCw,
  Square,
  Users,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MetricCards } from "@/components/metric-cards";
import { SearchInput } from "@/components/search-input";
import { EnumBadge } from "@/components/badges";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  INTAKE_DEFAULT_TZ,
  intakeWeekToDateLookbackDays,
  todayLocalDate,
  useIntakeDay,
  useIntakeDays,
} from "@/hooks/use-intake";
import { useDebounce } from "@/hooks/use-debounce";
import {
  caseDisplayName,
  failClassLabel,
  failClassTone,
  formatLocalDateLabel,
  formatLocalDateLong,
  formatScheduledFor,
  shiftYmd,
  shortCaseId,
} from "@/lib/intake-format";
import { CasePhoneNumbersTable } from "@/components/case-phone-numbers-table";
import { VapiConcurrencyButton } from "@/components/vapi-concurrency-button";
import config from "@/config";
import { cn } from "@/utils";
import type { FailClass, IntakeCase } from "@/types/intake";
import {
  IN_FLIGHT_FAIL_CLASSES,
  RETRYABLE_FAIL_CLASSES,
} from "@/types/intake";

type IntakeBucketFilter =
  | FailClass
  | "all"
  | "retryable"
  | "in_flight"
  | "nurtec_denied"
  | "appeals";

const IN_FLIGHT_SET = new Set<FailClass>(IN_FLIGHT_FAIL_CLASSES);

function showRetryAllInView(filter: IntakeBucketFilter): boolean {
  if (filter === "retryable" || filter === "telephony_error") return true;
  if (filter === "in_flight") return true;
  return IN_FLIGHT_SET.has(filter as FailClass);
}

const TZ = INTAKE_DEFAULT_TZ;
const PAGE_SIZE = 200;

function isNurtecDenied(c: IntakeCase): boolean {
  return (
    !c.is_appeal_case &&
    (c.product_name || "").toLowerCase().includes("nurtec") &&
    (c.pa_status || "").toLowerCase() === "denied"
  );
}

async function postAppealStatusCall(caseId: string): Promise<string> {
  const response = await fetch(
    `${config.API_BASE_URL}/api/dashboard/cases/${encodeURIComponent(caseId)}/appeal-status-call`,
    { method: "POST" },
  );
  const body = (await response.json().catch(() => ({}))) as {
    detail?: string;
    message?: string;
  };
  if (!response.ok) {
    throw new Error(body.detail || `Appeal call failed: ${response.status}`);
  }
  return body.message || "Nurtec appeal-status call initiated";
}

function pillClasses(active: boolean) {
  return cn(
    "flex shrink-0 cursor-pointer flex-col items-center rounded-md border px-3 py-2 text-xs transition-colors",
    active
      ? "border-primary bg-primary text-primary-foreground"
      : "border-border bg-background hover:bg-muted/50",
  );
}

export default function DailyIntakePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = useMemo(() => todayLocalDate(TZ), []);
  const initialDate = searchParams.get("date") || today;
  const [date, setDate] = useState(initialDate);

  const lookbackDays = useMemo(
    () => intakeWeekToDateLookbackDays(today, TZ),
    [today],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm);
  const [bucketFilter, setBucketFilter] = useState<IntakeBucketFilter>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [appealCallingIds, setAppealCallingIds] = useState<Set<string>>(
    new Set(),
  );

  const {
    days,
    loading: daysLoading,
    refining: daysRefining,
    error: daysError,
    refresh: refreshDays,
  } = useIntakeDays(lookbackDays, TZ);
  const {
    cases,
    loading: dayLoading,
    loadingMore: dayLoadingMore,
    error: dayError,
    hasMore: dayHasMore,
    loadMore: loadMoreCases,
    refresh: refreshDay,
  } = useIntakeDay(date, { pageSize: PAGE_SIZE, duplicateLookback: 0, tz: TZ });

  // Keep ?date in sync.
  useEffect(() => {
    const current = searchParams.get("date") || today;
    if (current !== date) {
      const next = new URLSearchParams(searchParams);
      next.set("date", date);
      setSearchParams(next, { replace: true });
    }
    // Also clear selection when changing days.
    setSelectedIds(new Set());
    setBucketFilter("all");
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard arrows: ←/→ navigate days.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target && (e.target as HTMLElement).tagName === "INPUT") return;
      if (e.target && (e.target as HTMLElement).tagName === "TEXTAREA") return;
      if (e.key === "ArrowLeft") setDate((d) => shiftYmd(d, -1));
      else if (e.key === "ArrowRight") {
        setDate((d) => {
          const next = shiftYmd(d, 1);
          return next > today ? today : next;
        });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [today]);

  const handleRefresh = useCallback(() => {
    void refreshDays();
    void refreshDay();
  }, [refreshDays, refreshDay]);

  // Preferred source for KPIs/buckets: the lightweight `/intake/days` rollup
  // (cheap, cached, full-day truth). If that row hasn't arrived yet OR errored,
  // fall back to deriving counts from the loaded `cases` so the cards never
  // sit at all-zero while data is clearly on the page.
  const dayRow = useMemo(
    () => (days ?? []).find((d) => d.date === date) ?? null,
    [days, date],
  );
  const casesFallback = useMemo(() => {
    if (cases.length === 0) return null;
    const fc: Partial<Record<FailClass, number>> = {};
    for (const c of cases) {
      const k = c.fail_class as FailClass;
      fc[k] = (fc[k] ?? 0) + 1;
    }
    const get = (k: FailClass) => fc[k] ?? 0;
    const retryable =
      get("telephony_error") + get("wrong_number_retryable") + get("retryable_other");
    const exhausted = get("exhausted_live_agent") + get("exhausted_other");
    const in_flight = get("pending") + get("in_progress") + get("pending_verification");
    return {
      kpis: {
        total: cases.length,
        completed: get("live_outcome"),
        not_submitted: get("not_submitted"),
        in_flight,
        retryable,
        exhausted,
        no_dialable_phone: get("no_dialable_phone"),
        duplicate_patients: cases.filter((c) => c.is_duplicate_patient).length,
      },
      fail_classes: fc,
    };
  }, [cases]);

  const kpis = useMemo(() => {
    if (dayRow) {
      const fc = dayRow.fail_classes;
      return {
        total: dayRow.total,
        completed: fc.live_outcome ?? 0,
        not_submitted: fc.not_submitted ?? 0,
        in_flight: dayRow.in_flight,
        retryable: dayRow.retryable,
        exhausted: dayRow.exhausted,
        no_dialable_phone: fc.no_dialable_phone ?? 0,
        duplicate_patients:
          dayRow.duplicates_seen_before + dayRow.duplicates_within_day,
      };
    }
    return casesFallback?.kpis;
  }, [dayRow, casesFallback]);

  const buckets = useMemo(() => {
    const fc =
      dayRow?.fail_classes ?? casesFallback?.fail_classes ?? ({} as Partial<Record<FailClass, number>>);
    return Object.entries(fc).map(([fail_class, count]) => ({
      fail_class: fail_class as FailClass,
      count: count ?? 0,
    }));
  }, [dayRow, casesFallback]);

  const nurtecDeniedCount = useMemo(
    () => cases.filter(isNurtecDenied).length,
    [cases],
  );
  const appealCaseCount = useMemo(
    () => cases.filter((c) => c.is_appeal_case).length,
    [cases],
  );

  const filteredCases = useMemo(() => {
    let rows = cases;
    if (bucketFilter === "retryable") {
      const set = new Set<FailClass>(RETRYABLE_FAIL_CLASSES);
      rows = rows.filter((c) => set.has(c.fail_class));
    } else if (bucketFilter === "in_flight") {
      rows = rows.filter((c) => IN_FLIGHT_SET.has(c.fail_class));
    } else if (bucketFilter === "nurtec_denied") {
      rows = rows.filter(isNurtecDenied);
    } else if (bucketFilter === "appeals") {
      rows = rows.filter((c) => c.is_appeal_case);
    } else if (bucketFilter !== "all") {
      rows = rows.filter((c) => c.fail_class === bucketFilter);
    }
    const term = debouncedSearch.trim().toLowerCase();
    if (term) {
      rows = rows.filter((c) => {
        return (
          c.case_id.toLowerCase().includes(term) ||
          (c.parent_case_id || "").toLowerCase().includes(term) ||
          caseDisplayName(c).toLowerCase().includes(term) ||
          (c.member_id || "").toLowerCase().includes(term) ||
          (c.member_bin || "").toLowerCase().includes(term) ||
          (c.member_pcn || "").toLowerCase().includes(term) ||
          (c.payer_name || "").toLowerCase().includes(term) ||
          (c.structured_output_payer_name || "")
            .toLowerCase()
            .includes(term) ||
          (c.product_name || "").toLowerCase().includes(term) ||
          (c.fail_reason || "").toLowerCase().includes(term) ||
          (c.appeal_status || "").toLowerCase().includes(term) ||
          (c.fail_class || "").toLowerCase().includes(term)
        );
      });
    }
    return rows;
  }, [cases, bucketFilter, debouncedSearch]);

  // Anchor index for shift-click range selection (Gmail/file-manager style).
  const selectionAnchorRef = useRef<number | null>(null);

  const handleRowSelect = useCallback(
    (index: number, shiftKey: boolean) => {
      const target = filteredCases[index];
      if (!target) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (shiftKey && selectionAnchorRef.current !== null) {
          const anchor = selectionAnchorRef.current;
          const [lo, hi] = anchor < index ? [anchor, index] : [index, anchor];
          // Additive range select: the anchor's on/off state decides whether the
          // range is selected or deselected, matching common table UX.
          const turnOn = next.has(filteredCases[anchor]?.case_id ?? "");
          for (let i = lo; i <= hi; i++) {
            const c = filteredCases[i];
            if (!c) continue;
            if (turnOn) next.add(c.case_id);
            else next.delete(c.case_id);
          }
        } else {
          if (next.has(target.case_id)) next.delete(target.case_id);
          else next.add(target.case_id);
          selectionAnchorRef.current = index;
        }
        return next;
      });
    },
    [filteredCases],
  );

  const allFilteredSelected =
    filteredCases.length > 0 &&
    filteredCases.every((c) => selectedIds.has(c.case_id));

  const toggleSelectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (filteredCases.every((c) => next.has(c.case_id))) {
        for (const c of filteredCases) next.delete(c.case_id);
      } else {
        for (const c of filteredCases) next.add(c.case_id);
      }
      return next;
    });
  }, [filteredCases]);

  const sendToRetry = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      navigate(`/retry?case_ids=${encodeURIComponent(ids.join(","))}&from=${date}`);
    },
    [navigate, date],
  );

  const runAppealStatusCall = useCallback(
    async (caseId: string) => {
      setAppealCallingIds((prev) => new Set(prev).add(caseId));
      try {
        const message = await postAppealStatusCall(caseId);
        window.alert(message);
        await Promise.all([refreshDay(), refreshDays()]);
      } catch (error) {
        window.alert(
          error instanceof Error ? error.message : "Appeal call failed",
        );
      } finally {
        setAppealCallingIds((prev) => {
          const next = new Set(prev);
          next.delete(caseId);
          return next;
        });
      }
    },
    [refreshDay, refreshDays],
  );

  const selectedAppealCases = useMemo(
    () =>
      cases.filter(
        (c) => selectedIds.has(c.case_id) && c.appeal_call_eligible,
      ),
    [cases, selectedIds],
  );

  const runSelectedAppealCalls = useCallback(async () => {
    if (selectedAppealCases.length === 0) return;
    const confirmed = window.confirm(
      `Start ${selectedAppealCases.length} Nurtec appeal-status call${
        selectedAppealCases.length === 1 ? "" : "s"
      }?`,
    );
    if (!confirmed) return;

    const ids = selectedAppealCases.map((c) => c.case_id);
    setAppealCallingIds((prev) => new Set([...prev, ...ids]));
    const results = await Promise.allSettled(ids.map(postAppealStatusCall));
    const failed = results.filter((result) => result.status === "rejected");
    setAppealCallingIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
    await Promise.all([refreshDay(), refreshDays()]);
    setSelectedIds(new Set());

    if (failed.length === 0) {
      window.alert(`Started ${ids.length} appeal-status call${ids.length === 1 ? "" : "s"}.`);
    } else {
      window.alert(
        `Started ${ids.length - failed.length} of ${ids.length} appeal-status calls. ${failed.length} failed.`,
      );
    }
  }, [selectedAppealCases, refreshDay, refreshDays]);

  const metricItems = useMemo(() => {
    const k = kpis ?? {
      total: 0,
      completed: 0,
      not_submitted: 0,
      in_flight: 0,
      retryable: 0,
      exhausted: 0,
      no_dialable_phone: 0,
      duplicate_patients: 0,
    };
    return [
      { label: "Total received", value: k.total, icon: Activity },
      {
        label: "PA outcome",
        value: k.completed,
        icon: CheckCircle,
        color: "text-green-500",
      },
      {
        label: "Not submitted",
        value: k.not_submitted,
        icon: HelpCircle,
        color: "text-amber-500",
      },
      {
        label: "In flight",
        value: k.in_flight,
        icon: Clock,
        color: "text-blue-500",
      },
      {
        label: "Retryable",
        value: k.retryable,
        icon: RotateCw,
        color: "text-amber-600",
        secondary: { value: k.no_dialable_phone, label: "no phone" },
      },
      {
        label: "Exhausted",
        value: k.exhausted,
        icon: XCircle,
        color: "text-red-500",
      },
      {
        label: "Duplicates",
        value: k.duplicate_patients,
        icon: Users,
        color: "text-purple-500",
      },
    ];
  }, [kpis]);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Daily Intake</h1>
          <p className="text-sm text-muted-foreground">
            {formatLocalDateLong(date)} ·{" "}
            {dayLoading
              ? "loading…"
              : `${cases.length}${dayHasMore ? "+" : ""} of ${dayRow?.total ?? cases.length} case${
                  (dayRow?.total ?? cases.length) === 1 ? "" : "s"
                } loaded`}
          </p>
          {dayError ? (
            <p className="mt-1 text-xs text-destructive">{dayError}</p>
          ) : null}
          {daysError ? (
            <p className="mt-1 text-xs text-destructive">
              Daily rollup failed: {daysError}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <VapiConcurrencyButton />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDate((d) => shiftYmd(d, -1))}
            disabled={dayLoading}
            title="Previous day (←)"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDate(today)}
            disabled={dayLoading || date === today}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const next = shiftYmd(date, 1);
              if (next <= today) setDate(next);
            }}
            disabled={dayLoading || date >= today}
            title="Next day (→)"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={dayLoading || daysLoading}
          >
            <RotateCw
              className={cn(
                "h-4 w-4",
                (dayLoading || daysLoading) && "animate-spin",
              )}
            />
          </Button>
        </div>
      </div>

      {/* Date strip */}
      <ScrollArea className="-mx-2">
        <div className="flex gap-2 px-2">
          {daysLoading && !days
            ? Array.from({ length: lookbackDays }).map((_, i) => (
                <Skeleton key={i} className="h-[72px] w-[88px] shrink-0" />
              ))
            : null}
          {/* Older days still loading (phase 2): show skeletons on the left,
              before the already-loaded (newer) day pills. */}
          {daysRefining && days
            ? Array.from({
                length: Math.max(0, lookbackDays - days.length),
              }).map((_, i) => (
                <Skeleton
                  key={`pending-${i}`}
                  className="h-[72px] w-[88px] shrink-0"
                />
              ))
            : null}
          {daysLoading && !days
            ? null
            : (days ?? []).map((d) => {
                const active = d.date === date;
                const hasDuplicates =
                  d.duplicates_seen_before > 0 || d.duplicates_within_day > 0;
                return (
                  <button
                    type="button"
                    key={d.date}
                    onClick={() => setDate(d.date)}
                    className={pillClasses(active)}
                    title={[
                      formatLocalDateLong(d.date),
                      `Total ${d.total}`,
                      `✅ ${d.completed} completed`,
                      `❌ ${d.failed} failed (${d.retryable} retryable, ${d.exhausted} exhausted)`,
                      `⏳ ${d.in_flight} in-flight`,
                      hasDuplicates
                        ? `🔁 ${d.duplicates_seen_before} dup vs prior + ${d.duplicates_within_day} within-day`
                        : "",
                    ]
                      .filter(Boolean)
                      .join("\n")}
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-wide">
                      {formatLocalDateLabel(d.date)}
                    </span>
                    <span className="mt-0.5 text-base font-bold tabular-nums">
                      {d.total}
                    </span>
                    <span
                      className={cn(
                        "mt-0.5 text-[10px] tabular-nums",
                        active ? "opacity-90" : "text-muted-foreground",
                      )}
                    >
                      ✅{d.completed} ❌{d.failed} ⏳{d.in_flight}
                      {hasDuplicates ? ` 🔁${d.duplicates_seen_before + d.duplicates_within_day}` : ""}
                    </span>
                  </button>
                );
              })}
        </div>
      </ScrollArea>

      <MetricCards
        metrics={metricItems}
        loading={daysLoading && !dayRow}
        compact
      />

      {/* Bucket filter strip */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setBucketFilter("all")}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-medium",
            bucketFilter === "all"
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-background hover:bg-muted/50",
          )}
        >
          All <span className="ml-1 tabular-nums">{cases.length}</span>
        </button>
        <button
          type="button"
          onClick={() => setBucketFilter("retryable")}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-medium",
            bucketFilter === "retryable"
              ? "border-primary bg-primary text-primary-foreground"
              : "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100",
          )}
        >
          Retryable{" "}
          <span className="ml-1 tabular-nums">
            {(kpis?.retryable ?? 0)}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setBucketFilter("in_flight")}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-medium",
            bucketFilter === "in_flight"
              ? "border-primary bg-primary text-primary-foreground"
              : "border-blue-200 bg-blue-50 text-blue-900 hover:bg-blue-100",
          )}
        >
          In flight{" "}
          <span className="ml-1 tabular-nums">{(kpis?.in_flight ?? 0)}</span>
        </button>
        <button
          type="button"
          onClick={() => setBucketFilter("nurtec_denied")}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-medium",
            bucketFilter === "nurtec_denied"
              ? "border-primary bg-primary text-primary-foreground"
              : "border-violet-200 bg-violet-50 text-violet-900 hover:bg-violet-100",
          )}
        >
          Nurtec denied{" "}
          <span className="ml-1 tabular-nums">{nurtecDeniedCount}</span>
        </button>
        <button
          type="button"
          onClick={() => setBucketFilter("appeals")}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-medium",
            bucketFilter === "appeals"
              ? "border-primary bg-primary text-primary-foreground"
              : "border-indigo-200 bg-indigo-50 text-indigo-900 hover:bg-indigo-100",
          )}
        >
          Appeals <span className="ml-1 tabular-nums">{appealCaseCount}</span>
        </button>
        {buckets
          .filter((b) => b.count > 0)
          .map((b) => (
            <button
              type="button"
              key={b.fail_class}
              onClick={() => setBucketFilter(b.fail_class)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-xs font-medium",
                bucketFilter === b.fail_class
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background hover:bg-muted/50",
              )}
            >
              {failClassLabel(b.fail_class)}
              <span className="ml-1 tabular-nums">{b.count}</span>
            </button>
          ))}
      </div>

      {/* Search + selection toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by Case ID, name, member, payer, product, fail reason…"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={toggleSelectAllFiltered}
          disabled={filteredCases.length === 0}
          className="gap-1"
        >
          {allFilteredSelected ? (
            <CheckSquare className="h-3.5 w-3.5" />
          ) : (
            <Square className="h-3.5 w-3.5" />
          )}
          {allFilteredSelected
            ? "Deselect all"
            : `Select all in view (${filteredCases.length})`}
        </Button>
        {selectedIds.size > 0 ? (
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1 text-sm">
            <span className="font-medium">
              {selectedIds.size} selected
            </span>
            <Button
              size="sm"
              onClick={
                bucketFilter === "nurtec_denied" || bucketFilter === "appeals"
                  ? () => void runSelectedAppealCalls()
                  : () => sendToRetry(Array.from(selectedIds))
              }
              disabled={
                (bucketFilter === "nurtec_denied" ||
                  bucketFilter === "appeals") &&
                selectedAppealCases.length === 0
              }
              className="gap-1"
            >
              {bucketFilter === "nurtec_denied" ||
              bucketFilter === "appeals" ? (
                <>
                  <PhoneCall className="h-3.5 w-3.5" />
                  Run appeal calls ({selectedAppealCases.length})
                </>
              ) : (
                <>
                  <RotateCw className="h-3.5 w-3.5" />
                  Validate &amp; retry
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">
            Tip: click a row to select · shift-click to select a range
          </span>
        )}
        {showRetryAllInView(bucketFilter) ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              sendToRetry(filteredCases.map((c) => c.case_id))
            }
            disabled={filteredCases.length === 0}
            className="gap-1"
          >
            <RotateCw className="h-3.5 w-3.5" />
            Retry all in view ({filteredCases.length})
          </Button>
        ) : null}
        {dayHasMore ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={loadMoreCases}
            disabled={dayLoadingMore}
            className="ml-auto gap-1"
          >
            <ChevronDown className="h-3.5 w-3.5" />
            {dayLoadingMore
              ? "Loading…"
              : `Load ${PAGE_SIZE} more (${cases.length}/${dayRow?.total ?? "?"})`}
          </Button>
        ) : null}
      </div>

      {/* Cases table */}
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full min-w-[1100px] border-collapse">
          <thead>
            <tr className="border-b bg-muted/60 text-left text-xs font-medium text-muted-foreground">
              <th className="w-8 px-2 py-2">
                <input
                  type="checkbox"
                  aria-label="Select all in view"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAllFiltered}
                  disabled={filteredCases.length === 0}
                />
              </th>
              <th className="px-2 py-2">Case ID</th>
              <th className="px-2 py-2">Patient</th>
              <th className="px-2 py-2">Payer</th>
              <th className="px-2 py-2">Status</th>
              <th className="px-2 py-2">Fail reason</th>
              <th className="px-2 py-2">Attempts</th>
              <th className="px-2 py-2">Phone</th>
              <th className="px-2 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {dayLoading && cases.length === 0
              ? Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b">
                    {Array.from({ length: 9 }).map((_, j) => (
                      <td key={j} className="px-2 py-2">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              : filteredCases.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="h-24 text-center text-sm text-muted-foreground"
                    >
                      No cases match the current filters.
                    </td>
                  </tr>
                )
                : filteredCases.map((c, i) => (
                    <IntakeCaseRow
                      key={c.case_id}
                      c={c}
                      selected={selectedIds.has(c.case_id)}
                      onSelect={(shiftKey) => handleRowSelect(i, shiftKey)}
                      onRetry={() => sendToRetry([c.case_id])}
                      onAppealCall={() => void runAppealStatusCall(c.case_id)}
                      appealCallLoading={appealCallingIds.has(c.case_id)}
                    />
                  ))}
          </tbody>
        </table>
      </div>

      {dayHasMore ? (
        <div className="flex justify-center pb-2">
          <Button
            variant="outline"
            onClick={loadMoreCases}
            disabled={dayLoadingMore}
          >
            {dayLoadingMore
              ? "Loading…"
              : `Load ${PAGE_SIZE} more (${cases.length} of ${dayRow?.total ?? "?"} loaded)`}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function IntakeCaseRow({
  c,
  selected,
  onSelect,
  onRetry,
  onAppealCall,
  appealCallLoading,
}: {
  c: IntakeCase;
  selected: boolean;
  onSelect: (shiftKey: boolean) => void;
  onRetry: () => void;
  onAppealCall: () => void;
  appealCallLoading: boolean;
}) {
  const retryable =
    c.fail_class === "telephony_error" ||
    c.fail_class === "wrong_number_retryable" ||
    c.fail_class === "retryable_other";
  return (
    <tr
      onClick={(e) => onSelect(e.shiftKey)}
      className={cn(
        "cursor-pointer border-b align-top text-xs transition-colors hover:bg-muted/40",
        selected ? "bg-primary/5 hover:bg-primary/10" : undefined,
      )}
    >
      <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) =>
            onSelect((e.nativeEvent as MouseEvent).shiftKey)
          }
          aria-label="Select case"
        />
      </td>
      <td className="px-2 py-2">
        <div className="flex max-w-[200px] items-center gap-0.5" title={c.case_id}>
          <span className="min-w-0 flex-1 truncate font-mono text-[11px]">
            {shortCaseId(c.case_id)}
          </span>
          {c.is_appeal_case ? (
            <span className="rounded bg-indigo-100 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-indigo-800">
              appeal
            </span>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            asChild
            title="Open in Case Records"
          >
            <Link
              to={`/cases?case_id=${encodeURIComponent(c.case_id)}`}
              onClick={(e) => e.stopPropagation()}
            >
              <FileText className="h-3.5 w-3.5" />
            </Link>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            title="Copy case ID"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void navigator.clipboard?.writeText(c.case_id);
            }}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1.5">
          <span className="font-medium">{caseDisplayName(c)}</span>
          {c.is_appeal_case ? (
            <span className="inline-flex rounded-sm bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-200">
              appeal case
            </span>
          ) : c.has_active_appeal ? (
            <span className="inline-flex rounded-sm bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-800 dark:bg-violet-950/60 dark:text-violet-200">
              appeal in progress
            </span>
          ) : c.latest_appeal_case_id ? (
            <span
              className="inline-flex rounded-sm bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
              title={
                c.latest_appeal_status
                  ? `Latest appeal status: ${c.latest_appeal_status.replace(/_/g, " ")}`
                  : "This PA case has a linked appeal case"
              }
            >
              has appeal
            </span>
          ) : null}
          {c.is_duplicate_patient ? (
            <span
              title="Same patient (name + DOB) seen previously in window"
              className="inline-flex items-center gap-0.5 rounded-sm bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-800 dark:bg-purple-950/60 dark:text-purple-200"
            >
              <Copy className="h-3 w-3" />
              dup
            </span>
          ) : null}
          {c.auto_retry_paused_reason ? (
            <span
              title={`Auto-retries paused: ${c.auto_retry_paused_reason}. Use the Retry Console to force a retry.`}
              className="inline-flex items-center gap-0.5 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/60 dark:text-amber-200"
            >
              auto-retry paused
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {c.member_dob || "—"}
          {c.member_id ? ` · ${c.member_id}` : ""}
        </div>
        {c.is_appeal_case && c.parent_case_id ? (
          <Link
            className="mt-0.5 block text-[10px] text-indigo-700 hover:underline"
            to={`/cases?case_id=${encodeURIComponent(c.parent_case_id)}`}
            onClick={(e) => e.stopPropagation()}
          >
            Parent PA: {shortCaseId(c.parent_case_id)}
          </Link>
        ) : null}
        {c.member_bin || c.member_pcn ? (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            BIN {c.member_bin || "—"} · PCN {c.member_pcn || "—"}
          </div>
        ) : null}
      </td>
      <td className="px-2 py-2">
        <span className="text-foreground">
          {c.structured_output_payer_name || c.payer_name || "—"}
        </span>
        <div className="text-[11px] text-muted-foreground">
          {c.product_name || "—"}
        </div>
      </td>
      <td className="px-2 py-2">
        <EnumBadge
          value={
            c.is_appeal_case
              ? c.appeal_call_in_progress ||
                c.appeal_flow_status === "in_progress"
                ? "Appeal in progress"
                : c.appeal_status
                  ? "Appeal outcome"
                  : "Appeal case"
              : failClassLabel(c.fail_class)
          }
          semanticTone={
            c.is_appeal_case
              ? c.appeal_call_in_progress ||
                c.appeal_flow_status === "in_progress"
                ? "info"
                : c.appeal_status
                  ? "success"
                  : "neutral"
              : failClassTone(c.fail_class)
          }
        />
        {c.outbound_scheduled_for ? (
          <div
            className="mt-0.5 text-[11px] text-muted-foreground"
            title={`outbound_scheduled_for=${c.outbound_scheduled_for}`}
          >
            scheduled {formatScheduledFor(c.outbound_scheduled_for)}
          </div>
        ) : c.current_batch_id && !c.is_appeal_case ? (
          <div
            className="mt-0.5 text-[11px] text-muted-foreground"
            title={`In active retry batch ${c.current_batch_id}`}
          >
            in batch {String(c.current_batch_id).slice(0, 8)}…
          </div>
        ) : null}
      </td>
      <td className="px-2 py-2">
        {c.fail_reason ? (
          <span className="capitalize">{c.fail_reason.replace(/_/g, " ")}</span>
        ) : c.appeal_status ? (
          <span className="capitalize">
            Appeal: {c.appeal_status.replace(/_/g, " ")}
          </span>
        ) : c.pa_status ? (
          <span className="capitalize">
            PA: {c.pa_status.replace(/_/g, " ")}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className="px-2 py-2 tabular-nums">
        {c.attempts}
        {c.all_numbers_exhausted ? (
          <span
            className="ml-1 inline-flex items-center text-amber-700"
            title="All known numbers tried"
          >
            <AlertTriangle className="h-3 w-3" />
          </span>
        ) : null}
      </td>
      <td className="px-2 py-2">
        <CasePhoneNumbersTable view={c} />
      </td>
      <td className="px-2 py-2 text-right">
        {c.appeal_call_eligible || c.appeal_call_in_progress ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            disabled={appealCallLoading || c.appeal_call_in_progress}
            onClick={(e) => {
              e.stopPropagation();
              onAppealCall();
            }}
          >
            <PhoneCall
              className={cn(
                "h-3 w-3",
                (appealCallLoading || c.appeal_call_in_progress) &&
                  "animate-pulse",
              )}
            />
            {appealCallLoading || c.appeal_call_in_progress
              ? "Appeal calling…"
              : c.is_appeal_case
                ? "Check appeal status"
                : "Run appeal call"}
          </Button>
        ) : retryable ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onRetry();
            }}
          >
            <RotateCw className="h-3 w-3" />
            Retry
          </Button>
        ) : (
          <span className="text-[11px] text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}
